import React from "react";
import styled, { css, useTheme } from "styled-components";
import QRCode from "react-qr-code";
import { useLocation } from "react-router";

import AppScreen from "@assets/images/mobile_app_screen.png";

import {
  ANDROID_APP_LINK,
  IOS_APP_LINK,
  NEXT_DATE_TO_SHOW_BANNER_KEY,
} from "@config/consts";
import { ROUTES, APP_SELECTOR_ROUTE } from "@config/app.routing";

import { AVAILABLE_COUNTRIES } from "@typings/globals";

import { rem } from "@ui/helpers";
import { Heading, Paragraph } from "@ui/Atoms";
import { AppStore, Close, GooglePlayColored } from "@ui/Assets/Symbolicons";

import { iOS } from "@services/iOS";
import { localStorageFacade } from "@services/LocalStorageFacade";
import { macOS } from "@services/MacOS";

import { useBreakpoint } from "@hooks/useBreakpoint";
import { useTranslate } from "@hooks/useTranslate";
import { useLocalisation } from "@hooks/useLocalisation";
import { useTelemetry } from "@hooks/useTelemetry";
import { getUserData } from "@hooks/useAuth/redux/user";

import { useAppSelector } from "@app/redux/hooks";

const mobileRoutesWithBanner = [
  `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`,
];
const desktopRoutesWithBanner = [
  `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`,
  `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_PROFILE}`,
  `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_OPTIONS}`,
  `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_BOOKINGS_TO_VALIDATE}`,
];

const countriesBlockList = [AVAILABLE_COUNTRIES.BE];

type MobileAppBannerProps = {
  squared?: boolean;
  hideOnMobile?: boolean;
};

const MobileAppBanner = React.memo(
  (props: MobileAppBannerProps): React.ReactElement | null => {
    const theme = useTheme();
    const { mobile } = useBreakpoint("v1");
    const isIOS = iOS();
    const isMacOS = macOS();
    const location = useLocation();
    const localStorage = localStorageFacade();
    const translate = useTranslate("batmaid_pwa_my_cleanings");
    const [_, setState] = React.useState(false); // to refresh a component
    const { country } = useLocalisation();
    const { elementClick } = useTelemetry();
    const userData = useAppSelector(getUserData);

    const userId = userData?.get("uuid");

    const nextShowDate =
      localStorage.getItem(NEXT_DATE_TO_SHOW_BANNER_KEY) || 0;
    const remainHidden = +new Date(nextShowDate) > +new Date();

    const isRouteEnabled =
      (!mobile && desktopRoutesWithBanner.includes(location.pathname)) ||
      (mobile && mobileRoutesWithBanner.includes(location.pathname));

    const selectorPageRoute =
      window.location.origin + APP_SELECTOR_ROUTE + `?id=${userId}`;

    if (
      !isRouteEnabled ||
      remainHidden ||
      countriesBlockList.includes(country)
    ) {
      return null;
    }

    const showAppStoreIcon = !mobile || isIOS || isMacOS;
    const showGooglePlayIcon = !mobile || (!isIOS && !isMacOS);

    const handleCloseClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      // display again after a month
      const nextDateToShow = new Date();
      nextDateToShow.setMonth(nextDateToShow.getMonth() + 1);

      localStorage.setItem(
        NEXT_DATE_TO_SHOW_BANNER_KEY,
        nextDateToShow.toDateString(),
      );
      setState(true);
    };

    const sendClickEvent = (buttonLabel: string) => {
      if (mobile) return;

      elementClick(buttonLabel, {
        context: { client_uuid: userId },
      });
    };

    const handleMobileBannerClick = () => {
      if (!mobile) return;

      const appUrl = isIOS || isMacOS ? IOS_APP_LINK : ANDROID_APP_LINK;
      const buttonLabel =
        isIOS || isMacOS ? "app-store-button" : "google-play-button";

      elementClick(buttonLabel, {
        context: { client_uuid: userId },
      });

      window.open(appUrl, "_blank")?.focus();
    };

    return (
      <Container
        onClick={handleMobileBannerClick}
        squared={props.squared}
        hideOnMobile={props.hideOnMobile}
      >
        <ImageContainer>
          <Image src={AppScreen} />
        </ImageContainer>

        <TextsContainer squared={props.squared}>
          <div>
            <StyledHeading color="white" type="h6" noMarginBotton>
              {translate("banner_promo_app_heading")}
            </StyledHeading>

            <Description color={theme.palette.grey200} type="body2">
              {translate("banner_promo_app_description")}
            </Description>
          </div>

          <LinksContainer>
            {showAppStoreIcon && (
              <a
                href={IOS_APP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => sendClickEvent("app-store-button")}
              >
                <AppStoreIcon
                  variant="solid"
                  size={104}
                  height={32}
                  color={theme.palette.white}
                />
              </a>
            )}
            {showGooglePlayIcon && (
              <a
                href={ANDROID_APP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => sendClickEvent("google-play-button")}
              >
                <GooglePlayColored
                  size={104}
                  height={32}
                  color={theme.palette.white}
                />
              </a>
            )}
          </LinksContainer>
        </TextsContainer>

        {!mobile && (
          <QRWrapper>
            <QRDescription
              squared={props.squared}
              color={theme.palette.grey200}
            >
              {translate("banner_promo_app_scan_code")}
            </QRDescription>

            <QRContainer>
              <QRCode size={92} value={selectorPageRoute} />
            </QRContainer>
          </QRWrapper>
        )}

        <CloseContainer onClick={handleCloseClick}>
          <Close
            variant="line"
            size={theme.icons.sizes.base_x4}
            color={theme.palette.white}
          />
        </CloseContainer>
      </Container>
    );
  },
);

MobileAppBanner.displayName = "MobileAppBanner";

export { MobileAppBanner };

const Container = styled.div<MobileAppBannerProps>`
  display: flex;

  ${props =>
    props.hideOnMobile &&
    css`
      display: none;

      ${props.theme.breakpoints.desktop} {
        display: flex;
      }
    `}

  align-items: center;
  justify-content: flex-start;
  position: relative;

  max-width: ${rem(684)};
  margin: ${props => props.theme.margins.base_x2} auto 0;
  padding: ${props =>
    `${props.theme.margins.base_x2} ${props.theme.margins.base_x2} 0 0`};
  background: ${props => props.theme.palette.grey800};
  overflow: hidden;
  color: ${props => props.theme.palette.white};
  border-radius: ${props => props.theme.border.radius.half};

  ${props => props.theme.breakpoints.tablet} {
    ${props =>
      !props.squared &&
      css`
        margin-top: ${props => props.theme.margins.base_x3};
        padding: ${props =>
          `${props.theme.margins.base_x2} ${props.theme.margins.base_x6} ${props.theme.margins.base_x2} ${props.theme.margins.base_x3}`};
      `}
  }

  ${props =>
    props.squared &&
    css`
      flex-wrap: wrap;
      flex-direction: row;
      justify-content: space-between;
      row-gap: ${props => props.theme.spacing.spacing_64};
      padding: ${props => props.theme.spacing.spacing_16}
        ${props => props.theme.spacing.spacing_32};
    `}
`;

const ImageContainer = styled.div`
  margin: ${props =>
    `-${props.theme.margins.base} ${props.theme.margins.half} 0 ${props.theme.margins.base_x1_5}`};
  position: relative;
  min-width: ${rem(96)};
  min-height: ${rem(129)};

  ${props => props.theme.breakpoints.tablet} {
    margin: ${props =>
      `-${props.theme.margins.base} 0 -${props.theme.margins.base_x2}`};
    min-width: ${rem(128)};
  }
`;

const Image = styled.img`
  width: ${rem(96)};
  min-height: ${rem(150)};
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;

  ${props => props.theme.breakpoints.tablet} {
    aspect-ratio: auto;
    width: ${rem(128)};
  }
`;

const StyledHeading = styled(Heading)`
  padding-right: 0;
  width: 90%;
  font-size: ${props => props.theme.fonts.sizes.body};
  font-weight: ${props => props.theme.fonts.weights.bold};

  ${props => props.theme.breakpoints.tablet} {
    width: 100%;
    padding-right: ${props => props.theme.margins.base_x2};
    font-size: ${props => props.theme.fonts.sizes.h6};
    line-height: ${rem(32)};
  }
`;

const Description = styled(Paragraph)`
  margin-bottom: 0;
`;

const TextsContainer = styled.div<{ squared?: boolean }>`
  flex: 1 1 auto;
  ${props =>
    props.squared &&
    css`
      order: -1;
      flex: 1 0 100%;
      align-items: center;
      text-align: center;
      ${StyledHeading} {
        padding-right: 0;
      }
    `}

  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.margins.base_x1_5};
  align-self: flex-start;
`;

const AppStoreIcon = styled(AppStore)`
  margin-left: -${props => props.theme.margins.half};
`;

const LinksContainer = styled.div`
  display: flex;
  margin-bottom: ${props => props.theme.margins.base_x2};
  gap: ${props => props.theme.margins.base_x1_5};

  ${props => props.theme.breakpoints.tablet} {
    margin-bottom: 0;
    margin-left: -${props => props.theme.margins.half};
  }
`;

const QRWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const QRDescription = styled(Paragraph)<{ squared?: boolean }>`
  position: absolute;

  bottom: 0;
  right: calc(100% + 1rem);

  ${props =>
    props.squared &&
    css`
      bottom: calc(100% + 1rem);
      right: 0;
    `}

  width: ${rem(128)};
  margin: 0;
  text-align: right;
  line-height: ${props => props.theme.fonts.lineHeights.caption};
  font-size: ${props => props.theme.fonts.sizes.caption};
  font-weight: ${props => props.theme.fonts.weights.semibold};
`;

const QRContainer = styled.div`
  padding: ${props => props.theme.margins.base};
  background: ${props => props.theme.palette.white};
`;

const CloseContainer = styled.div`
  position: absolute;
  top: ${props => props.theme.margins.base};
  right: ${props => props.theme.margins.base};
  cursor: pointer;
`;
